import React from 'react';
import Icon from '../../../components/AppIcon';

const GameStats = ({ game, userStats }) => {
  const formatFileSize = (sizeInMB) => {
    if (!sizeInMB || sizeInMB === 0) return '—';
    if (sizeInMB < 1024) return `${sizeInMB} MB`;
    return `${(sizeInMB / 1024).toFixed(1)} GB`;
  };

  const formatPlaytime = (minutes) => {
    if (!minutes || minutes === 0) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getCategoryNames = () => {
    return game?.game_category_mappings?.map(mapping => mapping.game_categories.name) || [];
  };

  const getStudioName = () => {
    return game?.gaming_studios?.name || game?.studio || '—';
  };

  const getRating = () => {
    return game?.rating || '—';
  };

  const getAverageRating = () => {
    if (!game?.average_rating || game.average_rating === 0) return '—';
    return game.average_rating.toFixed(1);
  };

  const getPlayCount = () => {
    if (!game?.play_count || game.play_count === 0) return '—';
    return game.play_count.toLocaleString();
  };

  const getReleaseDate = () => {
    if (!game?.published_at) return '—';
    return new Date(game.published_at).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Game Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Game Information</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Developer</span>
            <span className="text-sm font-medium text-foreground">{getStudioName()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Category</span>
            <div className="flex flex-wrap gap-1">
              {getCategoryNames().length > 0 ? (
                getCategoryNames().map((category, index) => (
                  <span key={index} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    {category}
                  </span>
                ))
              ) : (
                <span className="text-sm font-medium text-foreground">—</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Rating</span>
            <span className="text-sm font-medium text-foreground">{getRating()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">File Size</span>
            <span className="text-sm font-medium text-foreground">{formatFileSize(game?.file_size_mb)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Avg Play Time</span>
            <span className="text-sm font-medium text-foreground">{formatPlaytime(game?.estimated_play_time)}</span>
          </div>
        </div>
      </div>

      {/* Player Stats */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Player Stats</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total Players</span>
            <span className="text-sm font-medium text-foreground">{getPlayCount()}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Average Rating</span>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={14} className="text-yellow-500 fill-current" />
              <span className="text-sm font-medium text-foreground">{getAverageRating()}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Release Date</span>
            <span className="text-sm font-medium text-foreground">{getReleaseDate()}</span>
          </div>
        </div>
      </div>

      {/* User Stats */}
      {userStats && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Your Stats</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Playtime</span>
              <span className="text-sm font-medium text-foreground">{userStats.playtime}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last Played</span>
              <span className="text-sm font-medium text-foreground">{userStats.lastPlayed}</span>
            </div>
            
            {userStats.progress > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-medium text-foreground">{userStats.progress}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Requirements */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">System Requirements</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">WebGPU Support</span>
            <div className="flex items-center space-x-1">
              <Icon name="CheckCircle" size={14} className="text-green-500" />
              <span className="text-sm font-medium text-foreground">Required</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Browser</span>
            <span className="text-sm font-medium text-foreground">Chrome 113+</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">RAM</span>
            <span className="text-sm font-medium text-foreground">4GB+</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Graphics</span>
            <span className="text-sm font-medium text-foreground">WebGPU Compatible</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameStats;
